<template>
    <b-button-group
        :size="size"
        :tag="tag"
        :vertical="vertical"
    >
        <FileDownloadBundleButton
            v-if="showBundleButton && bundleDownloadLocation === 'above'"
            :category="singleCategory"
            :ids="allFileIds"
            :filename="bundleFilename"
            :buttonSize="size"
            v-bind="extraBundleProps"
        ></FileDownloadBundleButton>
        <BaseS3FileDownloadButton
            v-for="button in buttonList"
            :key="button.uniqueKey"
            v-bind="button"
        ></BaseS3FileDownloadButton>
        <FileDownloadBundleButton
            v-if="showBundleButton && bundleDownloadLocation === 'below'"
            :category="singleCategory"
            :ids="allFileIds"
            :filename="bundleFilename"
            :buttonSize="size"
            v-bind="extraBundleProps"
        ></FileDownloadBundleButton>
    </b-button-group>
</template>

<script>
import BaseS3FileDownloadButton from './BaseS3FileDownloadButton.vue';
import FileDownloadBundleButton from './FileDownloadBundleButton.vue';

export default {
    name: 'BaseS3FileDownloadButtonGroup',
    components: {
        BaseS3FileDownloadButton,
        FileDownloadBundleButton,
    },
    mixins: [],
    props: {
        // MUST PROVIDE EITHER fileData OR category + ids - if category/ids, we fetch the rest IN EACH BUTTON COMPONENT
        // category -- just pass it as a prop, but we don't declare it here... we pass on $attrs to each button component, category will be in there if specified 
        ids: {  // Array of string IDs -- if fetching fileData from client (preferred)
            type: Array,
            required: false,
        },
        category: {
            // the s3file category - each class has a unique one - REQUIRED IF PROVIDING 'ids'
            type: String,
            required: false,
        },
        // OR....  -- fileData wins if you provide both (but don't)
        fileData: {  // ARRAY OF OBJECTS - assumes object from your standard S3File serializer -- you need at least: 'filename', 'download_url' and 'filesize'
            type: Array,
            required: false,
        },
        // ACCESSORIES
        bundleDownloadLocation: {  // String, if you want to show a 'download all as zip' button -- ONLY IF MULTIPLE FILES
            // options are 'above', 'below', '' (empty string) -- if empty string, no bundle button is shown
            type: String,
            default: '',
        },
        bundleFilename: {  // String, if you want to specify the filename of the zip file -- will be the base of ___.zip - can be blank, have default on backend
            type: String,
            default: '',
        },
        bundleProps: {
            type: Object,
            default: function() {
                return {};  // don't specify anything here... just pass on directly to the bundle button
            },
        },
        // STYLE THINGS
        size: {
            type: String,
            default: 'md',
        },
        tag: {
            type: String,
            default: 'div',
        },
        vertical: {
            type: Boolean,
            default: false,
        },
        buttonProps: {  // we pass on all non-prop attrs to each button component ($attrs)
            type: Object,
            default: function() {
                return {};  // don't specify anything here... let the button component defaults do their thing
            },
        },
    },
    data: function() {
        return {}
    },
    computed: {
        buttonList() {
            // build list of objects for all buttons from ids or fileData - and make a unique key for using in v-for 
            const buttons = [];
            const buttonProps = {size: this.size, ...this.buttonProps};  // {...this.$attrs} also worked if you don't have buttonProps prop, but you MAY run into problems with html attributes getting lowercased, then not getting picked up correctly if child components expects camel/kebab case
            if (this.fileData && Array.isArray(this.fileData) && this.fileData.length) {
                this.fileData.forEach(fd => {
                    const uniqueKey = fd.download_url;
                    buttons.push({fileData: fd, uniqueKey, ...buttonProps});  // make objects of fileData + all 'extra' attributes
                });
            } else if (this.category && this.ids && Array.isArray(this.ids) && this.ids.length) {
                // MUST have 'category' in $attrs for this to work
                this.ids.forEach(i => {
                    const uniqueKey = i;
                    buttons.push({id: i, category: this.category, uniqueKey, ...buttonProps});
                });
            }
            return buttons;
        },
        allFileIds() {
            // we have to deal with the mixed schema types of buttons... could be id/category or fileData
            let ids = [];
            if (this.fileData && Array.isArray(this.fileData) && this.fileData.length) {
                ids = this.fileData.map(fd => fd.id);
            } else if (this.category && this.ids && Array.isArray(this.ids) && this.ids.length) {
                ids = [...this.ids];
            }
            return ids;  // always return an array here, we will check length to see if we have any
        },
        showBundleButton() {
            // if we have multiple files, and we have a bundle location, return true
            return this.bundleDownloadLocation && this.allFileIds.length > 1;
        },
        singleCategory() {
            // if all files are in the same category, return that category, otherwise return null
            // need this for the bundle button (it should be all one category, but we use this to check)
            let allCategories = [];
            if (this.fileData && Array.isArray(this.fileData) && this.fileData.length) {
                allCategories = this.fileData.map(fd => fd.file_category);
            } else if (this.category) {
                allCategories = [this.category];
            }
            const uniqueCategories = [...new Set(allCategories)];
            return uniqueCategories.length === 1 ? uniqueCategories[0] : null;
        },
        extraBundleProps() {
            // take the 'bundleProps' prop, remove any props that are already set directly 
            const finalProps = {};
            const originalProps = this.bundleProps || {};
            const excludeKeys = ['category', 'ids', 'filename', 'buttonSize', 'buttonText'];  // things we set directly
            Object.keys(originalProps).forEach(k => {
                if (!excludeKeys.includes(k)) {
                    finalProps[k] = originalProps[k];
                }
                finalProps['buttonText'] = originalProps['buttonText'] || this.defaultBundleButtonText;  // set default button text if not provided
            });
            return finalProps;
        },
        defaultBundleButtonText() {
            // this is the default because it can still be overridden by bundleProps.buttonText
            return this.allFileIds.length > 1 ? `Download all ${this.allFileIds.length} files (.zip)` : 'Download all files (.zip)';
        },
    },
    mounted: function() {
        
    },
    methods: {

    }
}
</script>

<style>

</style>